import React, { useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TrainingMessageBubble from './TrainingMessageBubble';
import TrainingChatInput from './TrainingChatInput';
import VoiceLivePanel from '../chat/VoiceLivePanel';

const TypingIndicator = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pl: 1 }}>
    <Typography variant="caption" color="text.secondary">Patient</Typography>
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'grey.400',
            animation: 'typingBounce 1.4s infinite ease-in-out',
            animationDelay: `${i * 0.2}s`,
            '@keyframes typingBounce': {
              '0%, 80%, 100%': { transform: 'scale(0.6)', opacity: 0.4 },
              '40%': { transform: 'scale(1)', opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
    <Typography variant="caption" color="text.secondary">is typing...</Typography>
  </Box>
);

const TrainingChatPanel = ({
  messages,
  isPatientTyping,
  isSending,
  sendError,
  onSendMessage,
  onClearError,
  onTurnClick,
  isSessionActive,
  isOwner = true,
  voiceConfig,
  autoplayEnabled,
  token,
  platformSettings,
  sx = {},
  // Voice mode props
  voiceModeActive = false,
  voiceLivePanelRef,
  trainingSocket,
  trainingSessionId,
  onVoiceClose,
}) => {
  const messageListRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  const wasAtBottom = useRef(true);

  const checkIfAtBottom = useCallback(() => {
    const el = messageListRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (wasAtBottom.current && scrollAnchorRef.current) {
      scrollAnchorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isPatientTyping]);

  const handleScroll = () => {
    wasAtBottom.current = checkIfAtBottom();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', ...sx }}>
      {/* Message list */}
      <Box
        sx={{ flex: 1, overflow: 'auto', p: 2 }}
        ref={messageListRef}
        onScroll={handleScroll}
      >
        {messages.length === 0 && !isPatientTyping && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Starting session...
            </Typography>
          </Box>
        )}

        {messages.map((msg, idx) => {
          // Autoplay only the last patient message
          const isLastPatientMessage = autoplayEnabled
            && msg.actor === 'patient'
            && idx === messages.length - 1;
          return (
            <TrainingMessageBubble
              key={msg.turnIndex ?? idx}
              message={msg}
              onTurnClick={onTurnClick}
              voiceConfig={voiceConfig}
              autoplayEnabled={isLastPatientMessage}
              token={token}
              platformSettings={platformSettings}
            />
          );
        })}

        {isPatientTyping && <TypingIndicator />}

        <div ref={scrollAnchorRef} />
      </Box>

      {/* Input area — observers (non-owners, e.g. a lecturer who opened a
          student's live session) get a read-only notice instead of the send
          box or the voice panel. Otherwise the voice panel replaces the text
          input when active. */}
      {!isOwner ? (
        <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Read-only observer view — you are not the trainee in this session.
          </Typography>
        </Box>
      ) : voiceModeActive && trainingSocket && trainingSessionId ? (
        <VoiceLivePanel
          ref={voiceLivePanelRef}
          socket={trainingSocket}
          conversationId={trainingSessionId}
          onClose={onVoiceClose}
          eventPrefix="training:voice:"
        />
      ) : (
        <TrainingChatInput
          onSend={onSendMessage}
          disabled={!isSessionActive || isSending}
          sendError={sendError}
          onClearError={onClearError}
        />
      )}
    </Box>
  );
};

export default TrainingChatPanel;
